import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, CircularProgress, TextField, Typography } from '@mui/material';
import api from '../../utils/api';
import { toast } from 'react-toastify';
import { commentError, formatWhen, roleLabel, stillDeletable, MAX_COMMENT_LENGTH } from './activityTimeline';

/**
 * The internal thread on a contract: staff comments interleaved with what
 * happened to it, newest first. The parent decides whether the viewer may see
 * it at all; the server enforces the same rule.
 */
const ContractActivity = ({ contractId }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState('');
  const [posting, setPosting] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const response = await api.get(`/contracts/${contractId}/timeline`);
      setItems(response.data.data || []);
    } catch (err) {
      toast.error('Failed to load contract activity');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [contractId]);

  useEffect(() => {
    load();
  }, [load]);

  const handlePost = async () => {
    const problem = commentError(draft);
    if (problem) {
      setError(problem);
      return;
    }
    setPosting(true);
    try {
      const response = await api.post(`/contracts/${contractId}/comments`, { body: draft.trim() });
      setItems((prev) => [response.data.data, ...prev]);
      setDraft('');
      setError('');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to post comment');
    } finally {
      setPosting(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm('Delete this comment? This cannot be undone.')) return;
    setDeleting(item.id);
    try {
      await api.delete(`/contracts/${contractId}/comments/${item.id}`);
      setItems((prev) => prev.filter((i) => i.id !== item.id));
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete comment');
      load();
    } finally {
      setDeleting(null);
    }
  };

  return (
    <Box>
      <TextField
        fullWidth
        multiline
        minRows={2}
        placeholder="Add a comment for the team — e.g. where this contract stands or what it is waiting on"
        value={draft}
        onChange={(e) => { setDraft(e.target.value); if (error) setError(''); }}
        error={Boolean(error)}
        helperText={error || 'Only staff can see these comments. Legal and the HOD are notified.'}
        inputProps={{ maxLength: MAX_COMMENT_LENGTH }}
        sx={{ '& .MuiOutlinedInput-root': { fontSize: '14px' } }}
      />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
        <Button
          variant="contained"
          onClick={handlePost}
          disabled={posting || !draft.trim()}
          sx={{ textTransform: 'none', bgcolor: '#111827', '&:hover': { bgcolor: '#374151' } }}
        >
          {posting ? 'Posting…' : 'Post comment'}
        </Button>
      </Box>

      <Box sx={{ mt: 3 }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        )}
        {!loading && items.length === 0 && (
          <Typography variant="body2" color="text.secondary">No activity yet.</Typography>
        )}
        {!loading && items.map((item) => (
          item.kind === 'comment' ? (
            <Box key={item.id} sx={{ p: 2, mb: 1.5, border: '1px solid #e5e7eb', borderRadius: '8px', bgcolor: '#fff' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#111827' }}>
                  {item.actor?.name}
                  {roleLabel(item.actor?.role) && (
                    <Typography component="span" sx={{ fontSize: '12px', fontWeight: 400, color: '#6b7280', ml: 1 }}>
                      {roleLabel(item.actor.role)}
                    </Typography>
                  )}
                </Typography>
                <Typography sx={{ fontSize: '12px', color: '#6b7280' }}>{formatWhen(item.at)}</Typography>
              </Box>
              <Typography sx={{ fontSize: '14px', color: '#111827', mt: 0.75, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {item.body}
              </Typography>
              {stillDeletable(item) && (
                <Button
                  size="small"
                  onClick={() => handleDelete(item)}
                  disabled={deleting === item.id}
                  sx={{ textTransform: 'none', color: '#b91c1c', mt: 0.5, ml: -1, minWidth: 0 }}
                >
                  {deleting === item.id ? 'Deleting…' : 'Delete'}
                </Button>
              )}
            </Box>
          ) : (
            <Box key={item.id} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', px: 2, py: 1.25, mb: 1.5 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#9ca3af', mt: '6px', flexShrink: 0 }} />
              <Box>
                <Typography sx={{ fontSize: '13px', color: '#374151' }}>{item.label}</Typography>
                <Typography sx={{ fontSize: '12px', color: '#6b7280' }}>
                  {item.actor?.name} · {formatWhen(item.at)}
                </Typography>
              </Box>
            </Box>
          )
        ))}
      </Box>
    </Box>
  );
};

export default ContractActivity;
